<script setup>
import axios from 'axios'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/vue/20/solid'
import { computed, ref, reactive, onMounted } from 'vue'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import Swal from 'sweetalert2'
import { useVuelidate } from '@vuelidate/core'
import { workRules } from '@/helpers/agent_work_validator'

dayjs.extend(updateLocale)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.updateLocale('en', {
  weekStart: 1,
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
})
const props = defineProps({
  agentId: {
    type: String,
    required: true
  },
  creators: {
    type: Object,
    required: true
  },
  currentUser: {
    type: Object,
    required: true
  },
  wholeWorkPeriod: {
    type: Object,
    required: true
  },
  shiftOptions: {
    type: Array,
    required: true
  }
})
const needsEmailNotification = ref(false)
const data = reactive({
  fetched: {},
  fetchedWorkPeriods: {},
  creators: {},
  targetWorks: [],
  schedulerDepartmentCreators: [],
  isLoading: false
})
const scheduleState = reactive({
  isEmptyWorkPeriod: false,
  hasRegistrableWork: false,
  hasLockedWork: false
})
const dateData = reactive({
  startOfCalendar: {},
  endOfCalendar: {},
  displayedStartOfCalendar: {},
  displayedEndOfCalendar: {},
  displayDates: []
})
const noteData = reactive({
  editOfferNote: {},
  editEntryNote: {}
})
const setData = () => {
  data.creators = props.creators
  scheduleState.isEmptyWorkPeriod = props.wholeWorkPeriod['first_date'] === null
  data.registrablePeriod = `（${dayjs(
    props.wholeWorkPeriod['first_date']
  ).format('YYYY/MM/DD')} - ${dayjs(props.wholeWorkPeriod['last_date']).format(
    'YYYY/MM/DD'
  )}）`
  dateData.startOfCalendar = dayjs(props.wholeWorkPeriod['first_date']).startOf(
    'week'
  )
  dateData.endOfCalendar = dayjs(props.wholeWorkPeriod['last_date'])
    .add(1, 'w')
    .startOf('week')
  dateData.displayedStartOfCalendar = dayjs(
    props.wholeWorkPeriod['first_date']
  ).startOf('week')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
}

const v$ = useVuelidate(workRules, data)

const fetchWorks = async () => {
  let result = await axios.get(
    `/agents/${props.agentId}/works.json\
      ?start_date=${dateData.startOfCalendar.format('YYYY-MM-DD')}\
      &end_date=${dateData.endOfCalendar.format('YYYY-MM-DD')}`
  )
  data.fetched = result.data
}
const fetchWorkPeriods = async () => {
  let result = await axios.get(
    `/agents/${props.agentId}/recent_department_periods.json`
  )
  data.fetchedWorkPeriods = result.data['work_periods']
}
const buildTargetWorks = () => {
  const result = []
  data.fetched.creator_works.forEach((creator_work) => {
    const display_works = []
    let indexDay = dateData.startOfCalendar.clone()
    while (indexDay.isSameOrBefore(dateData.endOfCalendar)) {
      if (
        creator_work.works.find(
          (work) => work.date === indexDay.format('YYYY-MM-DD')
        )
      )
        display_works.push(
          creator_work.works.find(
            (work) => work.date === indexDay.format('YYYY-MM-DD')
          )
        )
      else {
        display_works.push({
          date: indexDay.format('YYYY-MM-DD'),
          status: 'adjusting',
          offer_id: null,
          offer_note: '',
          entry_id: null,
          entry_note: ''
        })
      }
      indexDay = indexDay.add(1, 'day')
    }
    const creatorDepartmentId = data.creators.find(
      (creator) => creator.id === creator_work.creator_id
    )?.department_id
    if (creatorDepartmentId === props.currentUser['department_id'])
      data.schedulerDepartmentCreators.push(creator_work.creator_id)
    const creatorWorkPeriod = data.fetchedWorkPeriods.find(
      (creator) => creator.department_id === creatorDepartmentId
    )
    const creatorShiftTimeTypeId = data.creators.find(
      (creator) => creator.id === creator_work.creator_id
    )?.shift_time_type_id

    scheduleState.hasLockedWork =
      scheduleState.hasLockedWork ||
      dayjs(creatorWorkPeriod?.expired_at) < dayjs()
    result.push({
      is_admin_or_department_scheduler:
        creator_work.is_admin_or_department_scheduler,
      creator_id: creator_work.creator_id,
      ignore_expiration: creator_work.ignore_expiration,
      creator_name: creator_work.name,
      first_date: creatorWorkPeriod?.first_date,
      last_date: creatorWorkPeriod?.last_date,
      expired_at: creatorWorkPeriod?.expired_at,
      shift_time_type: props.shiftOptions.find(
        (shiftTimeType) => shiftTimeType.id === creatorShiftTimeTypeId
      ),
      works: display_works
    })
  })
  return result
}
const buildDisplayWorks = () => {
  dateData.displayDates = []
  let indexDay = dateData.displayedStartOfCalendar.clone()
  while (indexDay.isSameOrBefore(dateData.displayedEndOfCalendar)) {
    dateData.displayDates.push(indexDay.format('YYYY-MM-DD'))
    indexDay = indexDay.add(1, 'day')
  }
}

const toggleEditOffer = (targetDay, creatorId) => {
  if (targetDay) {
    noteData.editOfferNote = { date: targetDay, creatorId: creatorId }
  } else {
    noteData.editOfferNote = null
  }
}

const toggleEditEntry = (targetDay, creatorId) => {
  if (targetDay) {
    noteData.editEntryNote = { date: targetDay, creatorId: creatorId }
  } else {
    noteData.editEntryNote = null
  }
}

const setDefaultShiftTimeType = (creator_works) => {
  creator_works.works.forEach((work) => {
    if (isRegistrationInvalidDay(work, creator_works)) return
    if (work.status !== 'adjusting') return
    work.entry_id = creator_works.shift_time_type.id
  })
}

onMounted(async () => {
  data.isLoading = true
  await setData()
  await fetchWorks()
  await fetchWorkPeriods()
  data.targetWorks = buildTargetWorks()
  await buildDisplayWorks()
  data.isLoading = false
  scheduleState.hasRegistrableWork =
    data.schedulerDepartmentCreators.length !== 0 ||
    payload.value.agent_work_registration.creator_works.length !== 0
})

const isDisabledPreviousButton = () =>
  dayjs(dateData.displayDates[0]) <= dateData.startOfCalendar
const isDisabledNextButton = () =>
  dayjs(dateData.displayDates.slice(-1)[0]) >= dateData.endOfCalendar
const isRegistrationInvalidDay = (day, creatorWorks) => {
  return (
    creatorWorks.first_date === undefined ||
    (dayjs(creatorWorks.expired_at) < dayjs() &&
      !creatorWorks.is_admin_or_department_scheduler &&
      !creatorWorks.ignore_expiration) ||
    dayjs(day.date).isBefore(dayjs(), 'day') ||
    dayjs(day.date).isBefore(dayjs(creatorWorks.first_date), 'day') ||
    dayjs(dayjs(creatorWorks.last_date)).isBefore(dayjs(day.date), 'day')
  )
}
const setPreviousDisplayWorks = () => {
  dateData.displayedStartOfCalendar =
    dateData.displayedStartOfCalendar.subtract(7, 'day')
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const setNextDisplayWorks = () => {
  dateData.displayedStartOfCalendar = dateData.displayedStartOfCalendar.add(
    7,
    'day'
  )
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}
const setInitialDisplayWorks = () => {
  dateData.displayedStartOfCalendar = dayjs(dateData.startOfCalendar)
  dateData.displayedEndOfCalendar = dateData.displayedStartOfCalendar
    .clone()
    .add(1, 'w')
    .startOf('week')
  buildDisplayWorks()
}

const filterWorks = (works) =>
  works.filter(
    (work) =>
      dayjs(work.date) >= dateData.displayedStartOfCalendar &&
      dayjs(work.date) <= dateData.displayedEndOfCalendar
  )
const payload = computed(() => {
  const resultCreatorWorks = []
  data.targetWorks
    .filter(
      (creator_work) =>
        dayjs(creator_work.expired_at) > dayjs() ||
        creator_work.is_admin_or_department_scheduler ||
        creator_work.ignore_expiration
    )
    .forEach((creator_work) => {
      resultCreatorWorks.push({
        creator_id: creator_work.creator_id,
        work_registration: {
          needs_email_notification: false,
          works: creator_work.works.filter(
            (work) =>
              dayjs(work.date) >= dayjs(creator_work['first_date']) &&
              dayjs(work.date) <= dayjs(creator_work['last_date'])
          )
        }
      })
    })
  return {
    agent_work_registration: {
      needs_email_notification: needsEmailNotification.value,
      creator_works: resultCreatorWorks
    }
  }
})
const submit = async () => {
  const modal = await Swal.fire({
    title: '登録してもよろしいですか？',
    text: '',
    confirmButtonText: 'はい',
    confirmButtonColor: '#40BF6C',
    cancelButtonText: 'キャンセル',
    showCancelButton: true,
    reverseButtons: true,
    focusCancel: true
  })
  if (modal.isConfirmed) {
    data.isLoading = true
    try {
      await axios.post(
        `/agents/${props.agentId}/work_registrations`,
        payload.value
      )
      data.isLoading = false
      await Swal.fire({
        icon: 'success',
        title: '登録が完了しました',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
      window.location.href = `/agents/${props.agentId}/creators`
    } catch (error) {
      data.isLoading = false
      await Swal.fire({
        icon: 'error',
        title: '登録に失敗しました',
        text: '入力内容をご確認ください',
        confirmButtonText: 'OK',
        confirmButtonColor: '#40BF6C',
        reverseButtons: true,
        allowOutsideClick: false
      })
    }
  }
}
const dayBgColor = (day, creatorWorks) => {
  if (isRegistrationInvalidDay(day, creatorWorks))
    return 'bg-gray-50 text-gray-500'

  let result
  switch (day.status) {
    case 'adjusting':
      result = 'bg-yellow-50'
      break
    case 'monthly_fixed':
      result = 'bg-blue-50'
      break
    case 'weekly_fixed':
      result = 'bg-green-50'
      break
    default:
      result = 'bg-gray-50 text-gray-500'
  }
  return result
}

const labButtonPadding = props.currentUser.is_creator_or_agent
  ? '0.3rem'
  : '1rem'
const creatorButtonPadding = props.currentUser.is_creator_or_agent
  ? '1rem'
  : '0.3rem'
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <div class="relative pt-8">
    <div class="pb-6">
      <div class="mx-auto px-4 sm:px-6 md:px-8">
        <div
          v-if="scheduleState.isEmptyWorkPeriod"
          class="lg:flex lg:h-full lg:flex-col"
        >
          <header class="px-1 py-3">
            <div class="mb-8 flex items-center justify-between">
              <div class="text-2xl font-semibold text-gray-800">
                月間スケジュール登録
              </div>
            </div>
          </header>
          <div class="mt-10 grid grid-cols-12">
            <div class="col-span-8 col-start-3">
              <div
                class="overflow-hidden rounded-lg bg-white px-6 py-5 shadow"
              >
                <div
                  class="flex justify-center font-medium leading-6 text-gray-700"
                >
                  現在受付中の月間スケジュールはございません
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="lg:flex lg:h-full lg:flex-col">
          <div
            v-if="scheduleState.hasLockedWork"
            class="mb-10 border-l-4 border-yellow-400 bg-yellow-50 p-4"
          >
            <div class="flex">
              <div class="flex-shrink-0">
                <svg
                  class="h-6 w-6 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <div class="text-yellow-700">
                  ロックされているクリエイターを含んでいます。
                </div>
              </div>
            </div>
          </div>
          <header class="px-1 py-3">
            <div
              class="mb-4 items-center justify-between max-lg:flex-col lg:mb-8 lg:flex"
            >
              <div class="text-2xl font-semibold text-gray-800">
                月間スケジュール登録
                <span class="text-xl text-gray-700">{{
                  data.registrablePeriod
                }}</span>
              </div>
              <div class="mt-4 flex flex-col lg:mt-0">
                <div class="flex flex-row items-center">
                  <div
                    v-if="
                      props.wholeWorkPeriod['published'] === true &&
                      (props.currentUser['is_admin'] === true ||
                        data.schedulerDepartmentCreators.length !== 0)
                    "
                  >
                    <div class="flex items-center">
                      <input
                        id="email-notification"
                        v-model="needsEmailNotification"
                        type="checkbox"
                        class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300"
                        :disabled="!scheduleState.hasRegistrableWork"
                      />
                      <label
                        for="email-notification"
                        class="select-none font-medium text-gray-700"
                      >
                        更新をメールで通知する
                      </label>
                    </div>
                    <div class="text-gray-600">（公開前は通知されません）</div>
                  </div>
                  <button
                    :class="[
                      v$.$invalid || !scheduleState.hasRegistrableWork
                        ? 'disabled:opacity-25'
                        : 'hover:bg-yellow-600',
                      'ml-6 rounded-md border border-transparent bg-yellow-500 px-10 py-2 font-medium text-white shadow-sm  focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2'
                    ]"
                    :disabled="v$.$invalid || !scheduleState.hasRegistrableWork"
                    @click="submit"
                  >
                    日程登録
                  </button>
                </div>
                <div
                  v-if="v$.$invalid"
                  class="mt-2 flex flex-row-reverse text-sm font-semibold text-red-600"
                >
                  <div class="flex">
                    <ExclamationCircleIcon
                      class="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                    <div>コメントが長すぎる日があります</div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
            >
              <div class="flex items-center">
                <div
                  class="mr-2 flex h-8 items-center rounded-md shadow-sm md:items-stretch"
                >
                  <button
                    class="flex items-center justify-center rounded-l-md border border-r-0 border-gray-300 bg-white py-2 pl-3 pr-4 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                    :disabled="isDisabledPreviousButton()"
                    @click="setPreviousDisplayWorks"
                  >
                    <span class="sr-only">Previous month</span>
                    <ChevronLeftIcon class="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    class="hidden whitespace-nowrap border-b border-t border-gray-300 bg-white px-3.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:relative md:block"
                    @click="setInitialDisplayWorks"
                  >
                    初週
                  </button>
                  <span
                    class="relative -mx-px h-5 w-px bg-gray-300 md:hidden"
                  />
                  <button
                    :disabled="isDisabledNextButton()"
                    class="flex items-center justify-center rounded-r-md border border-l-0 border-gray-300 bg-white py-2 pl-4 pr-3 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:px-2 md:hover:bg-gray-50"
                    @click="setNextDisplayWorks"
                  >
                    <span class="sr-only">Next month</span>
                    <ChevronRightIcon class="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div
                class="hidden lg:ml-4 lg:flex lg:h-6 lg:w-px lg:bg-gray-300"
              />
              <div
                class="flex flex-wrap items-center whitespace-nowrap text-xs md:flex md:items-center"
              >
                <div class="py-3">
                  <div
                    class="ml-4 mr-2 inline-block h-3 w-3 rounded-full bg-orange-300"
                  ></div>
                  <span class="text-gray-700">Lab.提案</span>
                </div>
                <div class="py-3">
                  <div
                    class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-red-300"
                  ></div>
                  <span class="text-gray-700">クリエイター提出</span>
                </div>
                <div class="py-3">
                  <div
                    class="ml-5 mr-2 inline-block h-3 w-3 rounded-full bg-blue-300"
                  ></div>
                  <span class="text-gray-700">月間確定</span>
                </div>
                <div
                  class="ml-5 h-8 w-16 border border-gray-300 bg-yellow-50 opacity-80"
                >
                  <span class="flex justify-center leading-8 text-gray-800"
                    >調整中</span
                  >
                </div>
                <div
                  class="ml-2 h-8 w-16 border border-gray-300 bg-blue-50 opacity-80"
                >
                  <span class="flex justify-center leading-8 text-gray-800"
                    >月確</span
                  >
                </div>
                <div
                  class="ml-2 h-8 w-16 border border-gray-300 bg-green-50 opacity-80"
                >
                  <span class="flex justify-center leading-8 text-gray-800"
                    >週確</span
                  >
                </div>
              </div>
            </div>
          </header>
          <div
            class="shadow ring-1 ring-gray-300 ring-opacity-80 lg:flex lg:flex-auto lg:flex-col"
          >
            <div
              class="sr-only grid grid-cols-9 gap-px border-gray-300 border-opacity-80 bg-gray-300 bg-opacity-80 text-center text-xs font-semibold leading-6 text-gray-700 lg:not-sr-only lg:flex-none"
            >
              <div class="bg-white py-2" />
              <div class="bg-white py-2">月</div>
              <div class="bg-white py-2">火</div>
              <div class="bg-white py-2">水</div>
              <div class="bg-white py-2">木</div>
              <div class="bg-white py-2">金</div>
              <div class="bg-white py-2 text-blue-400">土</div>
              <div class="bg-white py-2 text-red-400">日</div>
              <div class="bg-white py-2">月</div>
            </div>
            <!-- カレンダー内側 -->
            <div
              v-for="(creator_works, creatorIndex) in data.targetWorks"
              :key="creator_works.creator_name"
            >
              <div
                class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto"
              >
                <div class="w-full border-t lg:grid lg:grid-cols-9 lg:gap-px">
                  <div
                    :class="[
                      dayjs(creator_works.expired_at) > dayjs() ||
                      creator_works.expired_at === undefined ||
                      creator_works.ignore_expiration
                        ? 'bg-white'
                        : 'bg-gray-50',
                      'flex flex-col items-center justify-center p-4'
                    ]"
                  >
                    <div class="text-lg">
                      {{ creator_works.creator_name }}
                    </div>
                    <div v-if="creator_works.expired_at" class="text-sm">
                      （提出期限：{{
                        dayjs(creator_works.expired_at).format('MM/DD HH:mm')
                      }})
                    </div>
                    <div v-if="dayjs(creator_works.expired_at) < dayjs()">
                      <div
                        :class="[
                          creator_works.ignore_expiration
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700',
                          'mt-3 inline-block rounded p-2 text-sm font-semibold'
                        ]"
                      >
                        {{
                          creator_works.ignore_expiration
                            ? 'ロック解除中'
                            : 'ロック中'
                        }}
                      </div>
                    </div>
                    <div v-if="creator_works.shift_time_type">
                      <button
                        class="rounded-md border border-transparent bg-gray-400 px-2 text-white shadow-sm hover:bg-gray-500 focus:outline-none"
                        @click="setDefaultShiftTimeType(creator_works)"
                      >
                        全て[{{
                          creator_works.shift_time_type.display_name
                        }}]をセット
                      </button>
                    </div>
                  </div>

                  <div
                    v-for="(day, workIndex) in filterWorks(creator_works.works)"
                    :key="day.date"
                    :class="[
                      dayBgColor(day, creator_works),
                      'relative flex px-3 py-2 lg:block'
                    ]"
                  >
                    <div
                      class="flex flex-col pr-3 lg:flex-row lg:justify-between lg:pr-0"
                    >
                      <time
                        :datetime="day.date"
                        :class="
                          dayjs(day.date).isToday()
                            ? 'flex h-6 w-6 items-center justify-center rounded-full bg-yellow-500 p-4 font-bold text-white'
                            : 'font-bold'
                        "
                      >
                        {{ dayjs(day.date).format('M/D') }}
                      </time>
                      <div
                        class="not-sr-only lg:sr-only"
                        :class="{
                          'text-blue-400': dayjs(day.date).day() === 6,
                          'text-red-400': dayjs(day.date).day() === 0
                        }"
                      >
                        {{ dayjs(day.date).format('ddd') }}
                      </div>
                    </div>

                    <!-- カレンダー1日分-->
                    <div class="w-full">
                      <!--  Labメモ -->
                      <template
                        v-if="
                          noteData.editOfferNote?.date === day.date &&
                          noteData.editOfferNote?.creatorId ===
                            creator_works.creator_id
                        "
                      >
                        <div class="flex items-center justify-between">
                          <textarea
                            v-model="day.offer_note"
                            rows="4"
                            class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border-gray-300 p-1 shadow-sm sm:text-sm"
                          />
                          <button
                            v-if="
                              !isRegistrationInvalidDay(day, creator_works) &&
                              creator_works.is_admin_or_department_scheduler
                            "
                            class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                            @click.prevent="toggleEditOffer"
                          >
                            <CheckCircleIcon
                              class="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </template>
                      <template v-else>
                        <div class="flex items-center justify-between">
                          <div class="editable-text">
                            {{ day.offer_note }}
                          </div>
                          <button
                            v-if="
                              !isRegistrationInvalidDay(day, creator_works) &&
                              creator_works.is_admin_or_department_scheduler
                            "
                            class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                            @click.prevent="
                              toggleEditOffer(
                                day.date,
                                creator_works.creator_id
                              )
                            "
                          >
                            <PencilSquareIcon
                              class="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </template>
                      <div
                        v-if="
                          v$.targetWorks.$each.$response?.$errors[creatorIndex]
                            .works[0]?.$response.$errors[workIndex]
                            .offer_note[0]
                        "
                        class="mt-2 flex text-sm font-semibold text-red-600"
                      >
                        <ExclamationCircleIcon
                          class="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                        <div>
                          {{
                            v$.targetWorks.$each.$response?.$errors[
                              creatorIndex
                            ].works[0].$response.$errors[workIndex]
                              .offer_note[0]?.$message
                          }}
                        </div>
                      </div>
                      <!--スケジューラーボタン-->
                      <div class="mb-4 mt-2">
                        <SelectButton
                          v-model="day.offer_id"
                          :options="shiftOptions"
                          option-label="display_name"
                          option-value="id"
                          aria-labelledby="single"
                          class="p-button-lab"
                          :disabled="
                            isRegistrationInvalidDay(day, creator_works) ||
                            !creator_works.is_admin_or_department_scheduler
                          "
                        />
                      </div>
                      <!--クリエイターボタン-->
                      <div class="mb-2 mt-4">
                        <SelectButton
                          v-model="day.entry_id"
                          :options="shiftOptions"
                          option-label="display_name"
                          option-value="id"
                          aria-labelledby="single"
                          class="p-button-creator w-full"
                          :disabled="
                            isRegistrationInvalidDay(day, creator_works) ||
                            day.status !== 'adjusting' ||
                            (!creator_works.is_admin_or_department_scheduler &&
                              !props.currentUser['is_creator_or_agent'])
                          "
                        />
                      </div>
                      <!-- クリエイターメモ -->
                      <template
                        v-if="
                          noteData.editEntryNote?.date === day.date &&
                          noteData.editEntryNote?.creatorId ===
                            creator_works.creator_id
                        "
                      >
                        <div class="flex items-center justify-between">
                          <textarea
                            v-model="day.entry_note"
                            rows="4"
                            class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border-gray-300 p-1 shadow-sm sm:text-sm"
                          />
                          <button
                            v-if="
                              !isRegistrationInvalidDay(day, creator_works) &&
                              day.status === 'adjusting' &&
                              (creator_works.is_admin_or_department_scheduler ||
                                props.currentUser['is_creator_or_agent'])
                            "
                            type="button"
                            class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                            @click.prevent="toggleEditEntry"
                          >
                            <CheckCircleIcon
                              class="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </template>
                      <template v-else>
                        <div class="flex items-center justify-between">
                          <div class="editable-text">
                            {{ day.entry_note }}
                          </div>
                          <button
                            v-if="
                              !isRegistrationInvalidDay(day, creator_works) &&
                              day.status === 'adjusting' &&
                              (creator_works.is_admin_or_department_scheduler ||
                                props.currentUser['is_creator_or_agent'])
                            "
                            type="button"
                            class="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 inline-flex items-center rounded-full border border-transparent p-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2"
                            @click.prevent="
                              toggleEditEntry(
                                day.date,
                                creator_works.creator_id
                              )
                            "
                          >
                            <PencilSquareIcon
                              class="h-5 w-5"
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </template>
                      <div
                        v-if="
                          v$.targetWorks.$each.$response?.$errors[creatorIndex]
                            .works[0]?.$response.$errors[workIndex]
                            .entry_note[0]
                        "
                        class="mt-2 flex text-sm font-semibold text-red-600"
                      >
                        <ExclamationCircleIcon
                          class="h-5 w-5 text-red-500"
                          aria-hidden="true"
                        />
                        <div>
                          {{
                            v$.targetWorks.$each.$response?.$errors[
                              creatorIndex
                            ].works[0]?.$response.$errors[workIndex]
                              .entry_note[0]?.$message
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-button-lab.p-selectbutton) {
  .p-button.p-highlight {
    background: #ef8e58;
    border-color: #ef8e58;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #ef8e58,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }

  .p-button {
    padding: v-bind(labButtonPadding) 4%;
    width: calc(100% / 3);
  }
}

:deep(.p-button-creator.p-selectbutton) {
  .p-button.p-highlight {
    background: #e66a6a;
    border-color: #e66a6a;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #e66a6a,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }

  .p-button {
    padding: v-bind(creatorButtonPadding) 4%;
    width: calc(100% / 3);
  }
}
</style>
